import {
  ensureKeyPair,
  getNickBase,
  getNickNumber,
  getPrivateKeyBase64,
  getPublicKeyBase64,
  hasMasterPassword,
  hasNickIdentity,
  changeMasterPassword,
  setMasterPasswordHash,
  unlockIdentityVault,
  setNickIdentity,
  verifyMasterPassword,
} from '../storage/keyStore.ts'
import {
  getDailyCode,
  getDailyCodeUtcDay,
  getInboundQueueId,
  getQueueOwnerPubKey,
  getSessionToken,
  saveDailyCode,
  saveDailyCodeUtcDay,
  saveInboundQueueId,
  saveQueueOwnerPubKey,
  saveSessionToken,
} from '../storage/sessionStore.ts'
import { getSocketAuthPublicKeyBase64 } from '../crypto/socketAuth.ts'
import {
  createMessageQueue,
  createSession,
  registerDailyCode,
} from '../socket2/ZionSocketClient.ts'
import { getRequireUnlockOnOpen } from '../storage/privacySettingsStore.ts'
import { isTotpEnabled } from '../storage/totpStore.ts'

const ADJECTIVES = [
  'Silent', 'Dark', 'Swift', 'Bright', 'Lone', 'Brave', 'Calm', 'Wild',
  'Sharp', 'Quick', 'Bold', 'Clever', 'Hidden', 'Noble', 'Quiet', 'Fierce',
] as const

const ANIMALS = [
  'Wolf', 'Fox', 'Hawk', 'Bear', 'Lynx', 'Owl', 'Raven', 'Tiger',
  'Eagle', 'Puma', 'Otter', 'Falcon', 'Viper', 'Stag', 'Crow', 'Seal',
] as const

const UNLOCK_KEY = 'zion_unlocked'
const PERSISTENT_UNLOCK_KEY = 'zion_persistent_unlock'

function utcTodayString(): string {
  return new Date().toISOString().slice(0, 10)
}

function utcDayOfYear(d = new Date()): number {
  const start = Date.UTC(d.getUTCFullYear(), 0, 1)
  const today = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
  return Math.floor((today - start) / 86_400_000) + 1
}

function dailySuffix(d = new Date()): string {
  const day = utcDayOfYear(d)
  const letter = String.fromCharCode('A'.charCodeAt(0) + (Math.abs(day) % 26))
  const digit = (Math.abs(day) % 9) + 1
  return `-${letter}${digit}`
}

export async function hasCompletedSetup(): Promise<boolean> {
  const [hasPwd, priv, pub, session] = await Promise.all([
    hasMasterPassword(),
    getPrivateKeyBase64(),
    getPublicKeyBase64(),
    getSessionToken(),
  ])
  return !!(hasPwd && priv && pub && session?.trim())
}

export function isSessionUnlocked(): boolean {
  try {
    if (sessionStorage.getItem(UNLOCK_KEY) === '1') return true
    if (!getRequireUnlockOnOpen() && localStorage.getItem(PERSISTENT_UNLOCK_KEY) === '1') {
      return true
    }
  } catch {
    /* ignore */
  }
  return false
}

export function markSessionUnlocked(): void {
  try {
    sessionStorage.setItem(UNLOCK_KEY, '1')
    if (!getRequireUnlockOnOpen()) {
      localStorage.setItem(PERSISTENT_UNLOCK_KEY, '1')
    }
  } catch {
    /* ignore */
  }
}

/** Blocco manuale (Exit con password, Blocca app): cancella anche unlock persistente. */
export function lockSession(): void {
  try {
    sessionStorage.removeItem(UNLOCK_KEY)
    localStorage.removeItem(PERSISTENT_UNLOCK_KEY)
  } catch {
    /* ignore */
  }
}

export async function getStableUserKey(): Promise<string> {
  const base = await getNickBase()
  const num = await getNickNumber()
  if (!base || num <= 0) return ''
  return `${base}#${num}`.toLowerCase()
}

export async function getBaseNickname(): Promise<string> {
  const base = await getNickBase()
  const num = await getNickNumber()
  if (!base || num <= 0) return ''
  return `${base}#${num}`
}

export async function getCurrentNickname(): Promise<string> {
  const base = await getBaseNickname()
  if (!base) return ''
  return base + dailySuffix()
}

export async function ensureNicknameGenerated(): Promise<void> {
  if (await hasNickIdentity()) return
  const adj = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)]!
  const animal = ANIMALS[Math.floor(Math.random() * ANIMALS.length)]!
  const number = Math.floor(Math.random() * 9000) + 1000
  await setNickIdentity(adj + animal, number, utcTodayString())
}

export async function createAnonymousSession(): Promise<string> {
  const previous = (await getSessionToken())?.trim() ?? ''
  try {
    const token = (await createSession(previous || null)).trim()
    if (!token) throw new Error('empty_session_token')
    await saveSessionToken(token)
    if (previous && token !== previous) {
      await saveInboundQueueId('')
      await saveQueueOwnerPubKey('')
    }
    return token
  } catch (err) {
    // Rete/5xx: non cancellare la sessione già in IndexedDB
    if (previous) return previous
    throw err instanceof Error ? err : new Error('session_create_failed')
  }
}

export async function registerDailyCodeForSession(): Promise<string> {
  const session = (await getSessionToken())?.trim()
  if (!session) throw new Error('no_session')
  const nick = await getBaseNickname()
  const code = (await registerDailyCode(session, nick)).trim().toUpperCase()
  if (!code) throw new Error('empty_daily_code')
  await saveDailyCode(code)
  await saveDailyCodeUtcDay(utcTodayString())
  return code
}

/** Rinnova ZN- — sempre ri-pubblica su socket2 (registry in RAM, perso ai restart). */
export async function ensureFreshDailyCode(): Promise<string> {
  const today = utcTodayString()
  const savedCode = await getDailyCode()
  const session = (await getSessionToken())?.trim()
  if (!session) {
    if (savedCode && (await getDailyCodeUtcDay()) === today) return savedCode
    throw new Error('no_session')
  }
  try {
    const code = (await registerDailyCode(session, await getBaseNickname())).trim().toUpperCase()
    if (!code) throw new Error('empty_daily_code')
    await saveDailyCode(code)
    await saveDailyCodeUtcDay(today)
    return code
  } catch {
    if (savedCode && (await getDailyCodeUtcDay()) === today) return savedCode
    throw new Error('daily_code_unavailable')
  }
}

export async function ensureInboundQueue(): Promise<{ queueId: string; recreated: boolean }> {
  const pub = await getSocketAuthPublicKeyBase64()
  const ownerPub = await getQueueOwnerPubKey()
  const existing = (await getInboundQueueId())?.trim()
  if (existing && ownerPub === pub) return { queueId: existing, recreated: false }

  const session = (await getSessionToken())?.trim()
  if (!session) {
    // Solo se manca del tutto — createAnonymousSession fallisce soft se offline
    const created = await createAnonymousSession()
    const queueId = (await createMessageQueue(created)).trim()
    if (!queueId) throw new Error('empty_queue_id')
    await saveInboundQueueId(queueId)
    await saveQueueOwnerPubKey(pub)
    return { queueId, recreated: true }
  }

  try {
    const queueId = (await createMessageQueue(session)).trim()
    if (!queueId) throw new Error('empty_queue_id')
    await saveInboundQueueId(queueId)
    await saveQueueOwnerPubKey(pub)
    return { queueId, recreated: true }
  } catch (err) {
    // 401/rete: se avevamo già una coda salvata, riusala
    if (existing) return { queueId: existing, recreated: false }
    throw err instanceof Error ? err : new Error('queue_create_failed')
  }
}

/** Primo avvio completo — identico ad Android FirstLaunch + enterAfterFirstLaunch. */
export async function completeFirstLaunch(plainPassword: string, plainPasswordConfirm: string): Promise<string> {
  const { validateNewPassword } = await import('../crypto/passwordValidation.ts')
  const check = validateNewPassword(plainPassword, plainPasswordConfirm)
  if (!check.ok) {
    if (check.reason === 'mismatch') throw new Error('Le password non coincidono')
    throw new Error('Password minimo 8 caratteri')
  }

  try {
    await setMasterPasswordHash(plainPassword)
  } catch (err) {
    console.error('[ZION] setMasterPasswordHash failed', err)
    throw new Error(
      'Impossibile salvare la password (crypto WebKit). Riprova o riavvia l’app.',
    )
  }

  try {
    await ensureKeyPair()
  } catch (err) {
    console.error('[ZION] ensureKeyPair failed', err)
    throw new Error('Impossibile generare le chiavi sul dispositivo. Riprova.')
  }

  await ensureNicknameGenerated()
  try {
    const { sealIdentityVault } = await import('../storage/keyStore.ts')
    await sealIdentityVault(plainPassword)
  } catch {
    /* vault opzionale al primo avvio */
  }

  try {
    await createAnonymousSession()
  } catch (err) {
    console.error('[ZION] createAnonymousSession failed (identità locale comunque creata)', err)
    // Non bloccare first-launch: password/chiavi sono locali; rete si ripara con Riconnetti.
  }

  try {
    await registerDailyCodeForSession()
  } catch (err) {
    console.error('[ZION] registerDailyCodeForSession failed', err)
    /* codice giornaliero: non blocca l’identità locale */
  }

  try {
    await ensureInboundQueue()
  } catch (err) {
    console.error('[ZION] ensureInboundQueue failed', err)
    /* coda: retry al bootstrap */
  }

  markSessionUnlocked()
  return getCurrentNickname()
}

export async function unlockWithMasterPassword(plainPassword: string): Promise<boolean> {
  if (!plainPassword.trim()) return false
  const ok = await verifyMasterPassword(plainPassword)
  if (!ok) return false
  try {
    await unlockIdentityVault(plainPassword)
  } catch {
    /* vault assente o legacy plaintext — continua */
  }

  if (!(await getSessionToken())) {
    try {
      await createAnonymousSession()
    } catch (err) {
      console.error('[ZION] unlock: createAnonymousSession soft-fail', err)
    }
  }
  const code = await getDailyCode()
  if (!code) {
    try {
      await registerDailyCodeForSession()
    } catch {
      /* session may renew on bootstrap */
    }
  }
  try {
    await ensureInboundQueue()
  } catch (err) {
    console.error('[ZION] unlock: ensureInboundQueue soft-fail', err)
  }

  // Se 2FA attiva, la sessione si sblocca solo dopo il codice TOTP (auth screen).
  if (!isTotpEnabled()) {
    markSessionUnlocked()
  }
  return true
}

export async function changeMasterPasswordFromSettings(
  currentPassword: string,
  newPassword: string,
  confirmPassword: string,
): Promise<void> {
  await changeMasterPassword(currentPassword, newPassword, confirmPassword)
}

export async function needsFirstLaunch(): Promise<boolean> {
  return !(await hasCompletedSetup())
}

export async function hasMasterPasswordSet(): Promise<boolean> {
  return hasMasterPassword()
}
